/**
 * Utilitário de cálculo de pontos para resgates financeiros (PIX e Pagamento de Contas).
 *
 * Regra de negócio — margem por faixas de valor (em reais):
 *   Faixa 1: até R$ 1.000          → margem fixa de +5 pontos
 *   Faixa 2: R$ 1.001 – R$ 5.000   → margem linear de +5 até +10 pontos
 *   Faixa 3: R$ 5.001 – R$ 15.000  → margem linear de +10 até +15 pontos
 *   Faixa 4: acima de R$ 15.000    → margem linear de +15 até +20 pontos
 *
 * A margem é somada ao valor base (1 ponto = R$ 1,00) e arredondada para inteiro.
 * Nenhuma linguagem de taxa, percentual ou desconto deve ser exposta ao usuário.
 */

export function calcularPontosFinanceiro(valorReais: number): number {
  if (valorReais <= 0) return 0;

  let margem: number;

  if (valorReais <= 1000) {
    // Faixa 1: margem fixa de 5
    margem = 5;
  } else if (valorReais <= 5000) {
    // Faixa 2: interpolação linear de 5 a 10 entre 1001 e 5000
    const progresso = (valorReais - 1000) / (5000 - 1000);
    margem = 5 + progresso * (10 - 5);
  } else if (valorReais <= 15000) {
    // Faixa 3: interpolação linear de 10 a 15 entre 5001 e 15000
    const progresso = (valorReais - 5000) / (15000 - 5000);
    margem = 10 + progresso * (15 - 10);
  } else {
    // Faixa 4: interpolação linear de 15 a 20 acima de 15000
    // Limita a progressão em 30.000 para não crescer infinitamente
    const progresso = Math.min((valorReais - 15000) / 15000, 1);
    margem = 15 + progresso * (20 - 15);
  }

  return Math.round(valorReais + margem);
}

/**
 * Retorna apenas a margem (diferença entre pontos e valor base).
 */
export function calcularMargemPontos(valorReais: number): number {
  return calcularPontosFinanceiro(valorReais) - Math.round(valorReais);
}

/**
 * Formata a exibição de pontos para o usuário.
 * Ex: 105 → "105 pontos"
 */
export function formatarPontos(pontos: number): string {
  return `${pontos.toLocaleString('pt-BR')} pontos`;
}
